import { pluralize, PromiseWorkerPool } from '@art-suite/art-core-ts';

import { SyncModel, SyncRecordBase, SyncType } from '../persistence';
import { SyncLogger } from '../SyncLogger';
import { AddError, AddSuccess } from './SyncingTypes';
import { isSoftFailure } from './syncLib';

/**
 * A read-only ref-like view of the current `isPaused` state. Passed by reference rather
 * than by value so async loops can observe live state instead of a stale captured boolean.
 */
export type IsPausedRef = { readonly current: boolean };

export interface SyncerProps {
  syncLogger: SyncLogger;
  addError: AddError;
  addSuccess: AddSuccess;
  isPausedRef: IsPausedRef;
}
export type SyncCompletionStats = {
  synced: number;
  unsynced: number;
  total: number;
};

export interface Syncer<T extends SyncRecordBase> {
  sync: (pendingItems: T[]) => Promise<number>;
  model: SyncModel<T>;
  syncLogger: SyncLogger;
  addError: AddError;
  isPausedRef: IsPausedRef;
  onComplete?: (stats: SyncCompletionStats) => Promise<unknown>;
}

const chunk = <T>(array: T[], size: number): T[][] => array.length ? [array.slice(0, size), ...chunk(array.slice(size), size)] : [];

const SINGLE_SYNC_CONCURRENCY_LIMIT = 5;

const modelTypeDisplayNames: Record<SyncType, string> = {
  scanData: 'Scan Data',
  scanNote: 'Scan Note',
  scanPhoto: 'Scan Photo',
  scanVideo: 'Scan Video',
};
const modelTypePluralDisplayNames: Record<SyncType, string> = {
  scanData: 'Scan Data',
  scanNote: 'Scan Notes',
  scanPhoto: 'Scan Photos',
  scanVideo: 'Scan Videos',
};

/**
 * Syncs items in batches, with a concurrency limit.
 *
 * If batchSync is provided, it will be used to sync the items in batches.
 * Otherwise, it will use a worker pool to sync the items individually.
 *
 * Returns the number of items synced.
 */
export const syncItemBatch = async <T extends SyncRecordBase>(
  {
    pendingItems,
    model,
    addError,
    syncLogger,
    syncOne,
    batchSync,
    addSuccess,
    batchSize,
    singleSyncConcurrencyLimit = SINGLE_SYNC_CONCURRENCY_LIMIT,
    onCompletion,
    isPausedRef,
  }: {
    pendingItems: T[];
    model: SyncModel<T>;
    addError: AddError;
    addSuccess: AddSuccess;
    syncLogger: SyncLogger;
    syncOne: (scanId: string, item: T) => Promise<unknown>;
    batchSync?: (scanId: string, scanDataBatch: T[]) => Promise<unknown>;
    singleSyncConcurrencyLimit?: number;
    batchSize?: number;
    onCompletion?: (stats: SyncCompletionStats) => Promise<unknown>;
    isPausedRef: IsPausedRef;
  },
) => {
  const type = model.getType();
  let syncedCount = 0;

  const syncOneItem = async (scanId: string, item: T) => {
    const itemId = item._id;
    const logIds = `${scanId}, ${type}Id: ${itemId}`;
    try {
      syncLogger('debug', type, `syncOne: ${logIds}...`);
      await model.markSyncing(itemId);
      await syncOne(scanId, item);
      await model.markSynced(itemId);
      syncLogger('debug', type, `syncOne: ${logIds}. Success.`);
      addSuccess(type, 1);
      syncedCount++;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (isSoftFailure(error)) {
        addError(false, logIds, error);
        syncLogger('warn', type, `Soft Failure (${logIds}): ${errorMessage}`, error, { itemId, itemType: type });
        await model.markSoftFailed(itemId, errorMessage);
      } else {
        addError(true, logIds, error);
        syncLogger('error', type, `Hard Failure (${logIds}): ${errorMessage}`, error, { itemId, itemType: type });
        await model.markHardFailed(itemId, errorMessage);
      }
    }
  };

  const workerPoolSyncItems = async (scanId: string, items: T[], onProgress: (progress: number) => void) => {
    const promiseWorkerPool = new PromiseWorkerPool(singleSyncConcurrencyLimit);
    syncLogger(
      'info',
      type,
      `workerPoolSync(${singleSyncConcurrencyLimit}) ${items.length}/${pendingItems.length} ${type} for scanId: ${scanId} (concurrency: ${singleSyncConcurrencyLimit})...`,
    );
    let syncedCount = 0;
    for (const item of items) {
      if (isPausedRef.current) {
        syncLogger('info', type, `Sync paused — stopping after current in-flight items.`);
        break;
      }
      await promiseWorkerPool.queue(async () => {
        await syncOneItem(scanId, item);
        onProgress(++syncedCount / pendingItems.length);
      });
    }
    syncLogger('debug', type, `workerPoolSync(${singleSyncConcurrencyLimit}) ${items.length}/${pendingItems.length} ${type} for scanId: ${scanId}. Success.`);
    await promiseWorkerPool.end();
  };

  const batchSyncItems = async (scanId: string, items: T[], onProgress: (progress: number) => void) => {
    if (!batchSync) return workerPoolSyncItems(scanId, items, onProgress);
    const batches = batchSize ? chunk(items, batchSize) : [items];
    let syncedCount = 0;
    for (const batch of batches) {
      if (isPausedRef.current) {
        syncLogger('info', type, `Sync paused — stopping after current batch.`);
        break;
      }
      try {
        syncLogger('debug', type, `batchSync: ${batch.length}/${pendingItems.length} ${type} for scanId: ${scanId}...`);
        await Promise.all(batch.map(async item => model.markSyncing(item._id)));
        await batchSync(scanId, batch);
        syncedCount += batch.length;
        onProgress(syncedCount / pendingItems.length);
        await Promise.all(batch.map(async item => model.markSynced(item._id)));
        syncLogger('debug', type, `batchSync: ${batch.length} ${type} for scanId: ${scanId}. Success.`);
        addSuccess(type, batch.length);
      } catch (error) {
        syncLogger(isSoftFailure(error) ? 'warn' : 'error', type, `batchSync: ${batch.length} ${type} for scanId: ${scanId}.`, error);
        return await workerPoolSyncItems(scanId, batch, onProgress);
      }
    }
  };

  // Group by scanId for bulk operations
  const scanDataByScanId: Record<string, T[]> = {};
  for (const item of pendingItems) {
    if (!scanDataByScanId[item.scanId]) scanDataByScanId[item.scanId] = [];
    scanDataByScanId[item.scanId].push(item);
  }

  await syncLogger.withProgress(
    pluralize(
      pendingItems.length,
      modelTypeDisplayNames[type],
      modelTypePluralDisplayNames[type],
    ),
    async onProgress => {
      const scanIds = Object.keys(scanDataByScanId);
      for (const scanId of scanIds) {
        if (isPausedRef.current) {
          syncLogger('info', type, `Sync paused — stopping before next scan.`);
          break;
        }
        if (scanDataByScanId[scanId].length === 1) {
          await syncOneItem(scanId, scanDataByScanId[scanId][0]);
        } else {
          await batchSyncItems(scanId, scanDataByScanId[scanId], onProgress);
        }
      }
    },
  );

  if (onCompletion) {
    const stats: SyncCompletionStats = { synced: syncedCount, unsynced: pendingItems.length - syncedCount, total: pendingItems.length };
    try {
      await onCompletion(stats);
    } catch (error) {
      syncLogger('warn', type, 'onCompletion failed:', error);
    }
  }

  return syncedCount;
};
